import type { Lang } from "@iyk/vf-app-config"

import * as UI from "@iyk/ui"
import * as React from "react"

import { AudioPlayer, Playlist } from "@iyk/vf-ui"
import { translations } from "../translations.ts"
import { ReleaseDateTag } from "./ReleaseDateTag.tsx"
import { useDownload } from "./download/useDownload.ts"

export const AudioPlayerDrawer = ({ open, onOpenChange, trigger, playlist }: AudioPlayerDrawer.Props) => {
  return (
    <UI.Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <UI.Drawer.Trigger asChild>{trigger}</UI.Drawer.Trigger>
      <UI.Drawer.Portal>
        <UI.Drawer.Overlay className="!z-40" />
        <UI.Drawer.Content
          title={playlist.title}
          onInteractOutside={(e) => {
            // Prevent closing the drawer when clicking on the audio player bottom bar
            const target = e.target as HTMLElement
            const isAudioPlayerBottomBar = target.closest("#audio-player-bottom-bar")
            if (isAudioPlayerBottomBar) {
              e.preventDefault()
            }
          }}
          className={UI.cx([
            "h-full max-w-[var(--app-max-width)] mx-auto !z-40",
            "data-[vaul-drawer-direction=bottom]:max-h-[90svh]"
          ])}
        >
          <UI.Drawer.Header className="sr-only">
            <UI.Drawer.Title>{playlist.title}</UI.Drawer.Title>
            <UI.Drawer.Description>{playlist.artist}</UI.Drawer.Description>
          </UI.Drawer.Header>
          <PlaylistInfo playlist={playlist} />
          <TrackList playlist={playlist} />
        </UI.Drawer.Content>
      </UI.Drawer.Portal>
    </UI.Drawer.Root>
  )
}

function PlaylistInfo({ playlist }: { playlist: AudioPlayer.Playlist }) {
  const isDownloadable = AudioPlayer.Playlist.$is("Released")(playlist) && playlist.isDownloadable
  const { lang } = Playlist.useAudioPlayer()

  return (
    <div className="flex flex-col items-center text-center gap-4 px-4 pt-8 pb-6 bshadow-b bshadow-gray-4">
      <img
        src={playlist.coverImageUrl}
        alt={playlist.title}
        className="size-28 rounded-sm object-cover overflow-hidden shrink-0"
      />
      <div className="flex flex-col items-center justify-center gap-3 relative w-full">
        {AudioPlayer.Playlist.$match(playlist, {
          Released: () => null,
          PartiallyReleased: (p) => p.releaseDate && <ReleaseDateTag releaseDate={p.releaseDate} />,
          Unreleased: (p) => <ReleaseDateTag releaseDate={p.releaseDate} />
        })}
        <UI.Collapsible.Root>
          <div className="relative w-full mb-2">
            <div className="mb-1">
              <p>{playlist.title}</p>
              <p className="text-sm text-gray-11">{playlist.artist}</p>
            </div>
            <p className="text-sm text-gray-11">
              {playlist.tracks.length} {Utils.pluralizeTrack(playlist.tracks.length, lang)}
            </p>
            {isDownloadable && <DownloadPlaylistButton />}
          </div>
          {isDownloadable && <DownloadPlaylistContent playlist={playlist} />}
        </UI.Collapsible.Root>
      </div>
    </div>
  )
}

const DownloadPlaylistContent = ({ playlist }: { playlist: AudioPlayer.Playlist }) => {
  const { lang } = Playlist.useAudioPlayer()
  const t = translations[lang]
  return (
    <UI.Collapsible.Content className="w-full">
      <div
        className={UI.cx(
          "mt-1 px-4 py-5",
          "bshadow bshadow-gray-4 w-full",
          "rounded-xl bg-gray-2",
          "grid gap-4"
        )}
      >
        <div className="grid gap-3">
          <span className="text-gray-12 text-sm">{t.AudioPlayerDrawer.downloadToDevice}</span>
          <DownloadButton playlist={playlist} />
        </div>
        <div className="flex items-center gap-2">
          <hr className="flex-1 text-gray-4" />
          <span className="text-xs text-gray-9">{t.AudioPlayerDrawer.or}</span>
          <hr className="flex-1 text-gray-4" />
        </div>
        <DownloadToEmail playlist={playlist} />
      </div>
    </UI.Collapsible.Content>
  )
}

const DownloadButton = ({ playlist }: { playlist: AudioPlayer.Playlist }) => {
  const collapsible = UI.Collapsible.useCollapsible()
  const { download } = useDownload()

  const { lang } = Playlist.useAudioPlayer()
  const t = translations[lang]

  const [isDownloading, setIsDownloading] = React.useState(false)

  const handleClick = async () => {
    setIsDownloading(true)
    const response = await download(playlist.id)

    if (response.success) {
      showDownloadToast(t.AudioPlayerDrawer.downloadStarted)
      const downloadLink = response.downloadUrl
      window.location.href = downloadLink
      collapsible.toggle()
    } else {
      showDownloadToast(response.error)
    }

    setIsDownloading(false)
  }

  return (
    <UI.Button
      size="md"
      kind="solid"
      type="button"
      className="w-fit justify-self-center"
      onClick={handleClick}
      disabled={isDownloading}
    >
      {isDownloading ? <UI.Icon.Loading /> : <UI.Icon.Download />} {t.AudioPlayerDrawer.download}
    </UI.Button>
  )
}

const DownloadToEmail = ({ playlist }: { playlist: AudioPlayer.Playlist }) => {
  const collapsible = UI.Collapsible.useCollapsible()
  const { requestDownload, user } = useDownload()

  const { lang } = Playlist.useAudioPlayer()
  const t = translations[lang]

  const userEmail = user?.email

  const [isDownloading, setIsDownloading] = React.useState(false)

  const emailRef = React.useRef<HTMLInputElement>(null)

  const handleSendClick = async () => {
    const targetEmail = user?.email ?? emailRef.current?.value ?? ""

    setIsDownloading(true)
    const response = await requestDownload(playlist.id, targetEmail)

    if (response.success) {
      showDownloadToast(t.AudioPlayerDrawer.downloadLinkSent)
      collapsible.toggle()
    } else {
      showDownloadToast(response.error)
    }

    setIsDownloading(false)
  }

  return (
    <div className="grid gap-3">
      <span className="text-gray-12 text-sm">{t.AudioPlayerDrawer.sendLinkTo(userEmail)}</span>

      {userEmail ? (
        <UI.Button
          size="md"
          kind="solid"
          type="button"
          onClick={handleSendClick}
          className="w-fit justify-self-center"
          disabled={isDownloading}
        >
          {isDownloading && <UI.Icon.Loading />} {t.AudioPlayerDrawer.send}
        </UI.Button>
      ) : (
        // @dmain please fix This should be a form with an input and error message etc
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            handleSendClick()
          }}
        >
          <UI.Textbox
            placeholder={t.AudioPlayerDrawer.yourEmail}
            autoComplete="email"
            type="email"
            required
            className="flex-1"
            ref={emailRef}
          />
          <UI.Button size="md" kind="solid" type="button" disabled={isDownloading}>
            {isDownloading && <UI.Icon.Loading />} {t.AudioPlayerDrawer.submit}
          </UI.Button>
        </form>
      )}
    </div>
  )
}

const showDownloadToast = (message: string) =>
  UI.toast({
    title: message,
    variant: "default",
    hideCloseButton: true
  })

const DownloadPlaylistButton = () => {
  const { isOpen, toggle } = UI.Collapsible.useCollapsible()

  return (
    <UI.Button onClick={toggle} kind="outline" size="sm" type="button" className="absolute right-0 bottom-0">
      {isOpen ? <UI.Icon.Cross /> : <UI.Icon.Download />}
    </UI.Button>
  )
}

function TrackList({ playlist }: { playlist: AudioPlayer.Playlist }) {
  const { currentTrack } = Playlist.useAudioPlayer()

  const isThereACurrentTrack = Boolean(currentTrack)

  return (
    <div
      className={UI.cx([
        "overflow-auto p-2",
        // Custom scrollbar
        "[scrollbar-width:thin]",
        "[scrollbar-color:var(--gray-9)_transparent]",
        "[&::-webkit-scrollbar]:size-1.5",
        "[&::-webkit-scrollbar-thumb]:bg-gray-9 [&::-webkit-scrollbar-thumb]:rounded",
        "[&::-webkit-scrollbar-track]:bg-transparent",
        isThereACurrentTrack && "mb-[70px]"
      ])}
    >
      {playlist.tracks.map((track, index) => (
        <TrackListItem key={`track-${track.id}`} position={index + 1} track={track} playlist={playlist} />
      ))}
    </div>
  )
}

function TrackListItem({
  track,
  position,
  playlist
}: {
  track: AudioPlayer.Track
  position: number
  playlist: AudioPlayer.Playlist
}) {
  const { trackIsPlaying, currentTrack, playTrack } = Playlist.useAudioPlayer()

  const isCurrent = currentTrack?.id === track.id
  const isReleased = Utils.isTrackReleased(track)

  return (
    <button
      type="button"
      className={UI.cx([
        "w-full flex items-center gap-3 py-2 px-3",
        "data-[current=true]:bg-gray-3",
        "disabled:cursor-not-allowed",
        "group"
      ])}
      disabled={!isReleased}
      data-current={isCurrent}
      data-released={isReleased}
      onClick={() => playTrack(track.id, playlist)}
    >
      <div
        className={UI.cx([
          "w-4 text-xs shrink-0 text-left text-gray-11",
          "group-data-[released=false]:text-gray-10"
        ])}
      >
        {isCurrent && trackIsPlaying ? (
          <UI.Icon.SoundWaveSymbol aria-hidden="true" className="animate-pulse" />
        ) : (
          position
        )}
      </div>
      <div className="flex-1 space-y-0.5 text-left overflow-hidden ">
        <p className={UI.cx(["text-sm truncate text-ellipsis", "group-data-[released=false]:text-gray-10"])}>
          {track.title}
        </p>
        <p
          className={UI.cx([
            "text-xs text-gray-11 truncate text-ellipsis",
            "group-data-[released=false]:text-gray-10"
          ])}
        >
          {track.artist}
        </p>
      </div>
      <div className="shrink-0">
        {isReleased && (
          <div className="tabular-nums text-xs text-gray-11">
            {isCurrent ? (
              <TrackCurrentTime trackDuration={track.duration} />
            ) : (
              formatDuration(track.duration, track.duration >= 3600)
            )}
          </div>
        )}
      </div>
    </button>
  )
}

function TrackCurrentTime({ trackDuration }: { trackDuration: number }) {
  const { getCurrentTrackTime } = Playlist.useAudioPlayer()

  const showHours = trackDuration >= 3600
  const format = (t: number) => formatDuration(t, showHours)

  return <span>{`${format(getCurrentTrackTime())}/${format(trackDuration)}`}</span>
}

function formatDuration(seconds: number, showHours: boolean): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  const hh = String(h).padStart(2, "0")
  const mm = String(m).padStart(2, "0")
  const ss = String(s).padStart(2, "0")

  return showHours ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`
}

namespace AudioPlayerDrawer {
  export type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    trigger: React.ReactNode
    playlist: AudioPlayer.Playlist
  }
}

export namespace Utils {
  export function isTrackReleased(track: AudioPlayer.Track) {
    return AudioPlayer.Track.$is("Released")(track)
  }

  export function isPlaylistReleased(playlist: AudioPlayer.Playlist) {
    return AudioPlayer.Playlist.$is("Released")(playlist)
  }

  export function isPlaylistUnreleased(playlist: AudioPlayer.Playlist) {
    return AudioPlayer.Playlist.$is("Unreleased")(playlist)
  }

  const pluralRules = new Intl.PluralRules("en-US")
  export function pluralizeTrack(count: number, lang: Lang) {
    const t = translations[lang]
    return pluralRules.select(count) === "one" ? t.AudioPlayerDrawer.track : t.AudioPlayerDrawer.tracks
  }
}
